import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contactsAPI } from '../../services/api';
import { Button, Input, Select, Modal } from '../ui';

const STAGES = [
  { value: 'on_deck', label: 'On Deck' },
  { value: 'at_bat', label: 'At Bat' },
  { value: 'on_base', label: 'On Base' },
  { value: 'scoring_position', label: 'Scoring Position' },
  { value: 'hall_of_fame', label: 'Hall of Fame' },
  { value: 'bench', label: 'Bench' },
];

const CONTACT_TYPES = [
  { value: 'sphere', label: 'Sphere of Influence' },
  { value: 'lead', label: 'Lead' },
  { value: 'past_client', label: 'Past Client' },
  { value: 'referral_partner', label: 'Referral Partner' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'other', label: 'Other' },
];

interface ContactFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  stage: string;
  contact_type: string;
  source: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_zip: string;
  birthday: string;
  notes: string;
  tags: string;
}

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  contact?: {
    id: string;
    first_name: string;
    last_name?: string;
    email?: string;
    phone?: string;
    company?: string;
    stage: string;
    contact_type?: string;
    source?: string;
    address_street?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
    birthday?: string;
    notes?: string;
    tags?: string[];
  };
}

const emptyForm: ContactFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  stage: 'on_deck',
  contact_type: 'sphere',
  source: '',
  address_street: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  birthday: '',
  notes: '',
  tags: '',
};

export function ContactForm({ open, onClose, contact }: ContactFormProps) {
  const queryClient = useQueryClient();
  const isEditing = !!contact?.id;

  const [formData, setFormData] = useState<ContactFormData>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (contact) {
      setFormData({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        company: contact.company || '',
        stage: contact.stage || 'on_deck',
        contact_type: contact.contact_type || 'sphere',
        source: contact.source || '',
        address_street: contact.address_street || '',
        address_city: contact.address_city || '',
        address_state: contact.address_state || '',
        address_zip: contact.address_zip || '',
        birthday: contact.birthday || '',
        notes: contact.notes || '',
        tags: contact.tags?.join(', ') || '',
      });
    } else {
      setFormData(emptyForm);
    }
    setError(null);
  }, [contact, open]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => contactsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] });
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to create contact');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      contactsAPI.update(contact!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact', contact!.id] });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] });
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to update contact');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const { tags: tagsString, ...rest } = formData;

    // Filter out empty strings - API expects undefined, not empty strings for optional fields
    const cleanedData: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(rest)) {
      if (value !== '') {
        cleanedData[key] = value;
      }
    }

    // Handle tags separately
    const tags = tagsString
      ? tagsString.split(',').map((t) => t.trim()).filter(Boolean)
      : [];
    if (tags.length > 0) {
      cleanedData.tags = tags;
    }

    if (isEditing) {
      updateMutation.mutate(cleanedData);
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  const handleChange = (field: keyof ContactFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal open={open} onClose={onClose} title={isEditing ? 'Edit Contact' : 'Add Contact'} size="3xl">
      <form onSubmit={handleSubmit} className="px-6 pb-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Name */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              required
              value={formData.first_name}
              onChange={handleChange('first_name')}
            />
            <Input
              label="Last Name"
              value={formData.last_name}
              onChange={handleChange('last_name')}
            />
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
            />
            <Input
              label="Phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange('phone')}
            />
          </div>

          {/* Company */}
          <Input
            label="Company"
            value={formData.company}
            onChange={handleChange('company')}
          />

          {/* Stage and Type */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Stage"
              options={STAGES}
              value={formData.stage}
              onChange={handleChange('stage')}
            />
            <Select
              label="Contact Type"
              options={CONTACT_TYPES}
              value={formData.contact_type}
              onChange={handleChange('contact_type')}
            />
          </div>

          {/* Source and Birthday */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Source"
              placeholder="How did you meet?"
              value={formData.source}
              onChange={handleChange('source')}
            />
            <Input
              label="Birthday"
              type="date"
              value={formData.birthday}
              onChange={handleChange('birthday')}
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Address</label>
            <div className="space-y-2">
              <Input
                placeholder="Street"
                value={formData.address_street}
                onChange={handleChange('address_street')}
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  placeholder="City"
                  value={formData.address_city}
                  onChange={handleChange('address_city')}
                />
                <Input
                  placeholder="State"
                  value={formData.address_state}
                  onChange={handleChange('address_state')}
                />
                <Input
                  placeholder="ZIP"
                  value={formData.address_zip}
                  onChange={handleChange('address_zip')}
                />
              </div>
            </div>
          </div>

          {/* Tags */}
          <Input
            label="Tags"
            placeholder="Comma-separated tags (e.g., VIP, referral source)"
            value={formData.tags}
            onChange={handleChange('tags')}
            hint="Separate multiple tags with commas"
          />

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={handleChange('notes')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-ondeck-500 focus:border-ondeck-500"
              placeholder="Any additional notes about this contact..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" loading={isLoading} className="flex-1">
            {isEditing ? 'Save Changes' : 'Add Contact'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
